import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Calendar, Clock, User, Mail, Phone, Wallet, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { format, parse } from "date-fns";
import { fetchApi } from "@/lib/apiBase";

function formatMoney(value) {
  const amount = Number(value || 0);
  return `$${amount.toFixed(2)}`;
}

function formatSchedule(dateValue, timeValue) {
  try {
    const parsed = parse(`${dateValue} ${timeValue}`, "yyyy-MM-dd h:mm a", new Date());
    return {
      date: format(parsed, "EEEE, MMMM d, yyyy"),
      time: format(parsed, "h:mm a"),
    };
  } catch (error) {
    return {
      date: dateValue || "N/A",
      time: timeValue || "N/A",
    };
  }
}

export default function BookingConfirmationPage() {
  const [params] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const sessionId = params.get("session_id");
  const token = params.get("token");

  useEffect(() => {
    const confirmBooking = async () => {
      if (!sessionId || !token) {
        setErrorMessage("Missing confirmation details. Please return to booking.");
        setIsLoading(false);
        return;
      }

      try {
        const confirmEndpoints = [
          "/payments/confirm",
          "/payment/confirm",
          "/stripe/confirm",
        ];

        let confirmedBooking = null;
        let lastError = "Payment confirmation failed";

        for (const endpoint of confirmEndpoints) {
          const res = await fetchApi(
            `${endpoint}?session_id=${encodeURIComponent(sessionId)}&token=${encodeURIComponent(token)}`
          );
          if (res.ok) {
            confirmedBooking = await res.json();
            break;
          }
          const data = await res.json().catch(() => ({}));
          lastError = data.detail || `Payment confirmation failed (${res.status})`;
          if (res.status !== 404) break;
        }

        if (!confirmedBooking) throw new Error(lastError);
        setBooking(confirmedBooking);
        toast.success("Appointment confirmed");
      } catch (error) {
        setErrorMessage(error.message || "We could not confirm your booking.");
      } finally {
        setIsLoading(false);
      }
    };

    confirmBooking();
  }, [sessionId, token]);

  const schedule = useMemo(
    () => formatSchedule(booking?.preferred_date, booking?.preferred_time),
    [booking]
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f4f1ed] via-[#f7f5f2] to-white py-10">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <Link
          to="/book"
          className="inline-flex items-center gap-2 text-sm text-[#666] hover:text-[#111] transition-colors mb-6"
        >
          <ArrowLeft size={16} /> Back to Booking
        </Link>

        <div className="glass-card overflow-hidden">
          <div className="bg-[#111111] text-white px-6 py-6">
            <div className="flex items-center justify-center gap-3">
              <img
                src="/images/submark-logo.png"
                alt="DadaDeLuxe submark"
                className="w-10 h-10 object-contain rounded-full bg-white/90 p-1"
              />
              <div className="text-center sm:text-left">
                <p className="logo-text text-xl leading-none">DADADELUXE</p>
                <p className="text-[11px] tracking-[0.2em] uppercase text-white/70 mt-1">
                  Booking Confirmation
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-7">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="spinner border-[#111111] mx-auto" style={{ borderTopColor: "#111" }} />
                <p className="text-sm text-[#666] mt-3">Finalizing your appointment...</p>
              </div>
            ) : errorMessage ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-5">
                  <CheckCircle2 className="text-green-600" size={28} />
                  <div>
                    <h1 className="script-heading text-3xl leading-none">Appointment Confirmed</h1>
                    <p className="text-sm text-[#666] mt-1">
                      Your booking has been secured successfully.
                    </p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[#ececec] bg-white p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#888] mb-2">Service</p>
                    <p className="font-medium text-[#111]">{booking?.service_name || "N/A"}</p>
                  </div>
                  <div className="rounded-xl border border-[#ececec] bg-white p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#888] mb-2">Client</p>
                    <p className="font-medium text-[#111]">{booking?.full_name || "N/A"}</p>
                  </div>
                </div>

                <div className="mt-4 rounded-xl border border-[#ececec] bg-white p-4 space-y-2 text-sm">
                  <p className="flex items-center gap-2"><Calendar size={14} className="text-[#888]" />{schedule.date}</p>
                  <p className="flex items-center gap-2"><Clock size={14} className="text-[#888]" />{schedule.time}</p>
                  <p className="flex items-center gap-2"><Mail size={14} className="text-[#888]" />{booking?.email || "N/A"}</p>
                  <p className="flex items-center gap-2"><Phone size={14} className="text-[#888]" />{booking?.phone || "N/A"}</p>
                  <p className="flex items-center gap-2"><Wallet size={14} className="text-[#888]" />Paid Upfront: {formatMoney(booking?.deposit_amount)}</p>
                  <p className="flex items-center gap-2"><Wallet size={14} className="text-[#888]" />Remaining Balance: {formatMoney(booking?.remaining_balance)}</p>
                  {Array.isArray(booking?.selected_addons) && booking.selected_addons.length > 0 && (
                    <p className="flex items-start gap-2">
                      <User size={14} className="text-[#888] mt-0.5" />
                      <span>
                        Add-Ons:{" "}
                        {booking.selected_addons
                          .map((addOn) => addOn?.name || "Add-on")
                          .join(", ")}
                      </span>
                    </p>
                  )}
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  <Link to="/" className="btn-black">Back to Home</Link>
                  <Link to="/policies" className="btn-outline">View Policies</Link>
                  <Link to="/contact" className="btn-outline">Contact Stylist</Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
